/**
 * ANI Token Simulation Logic
 *
 * This module ports the ANI contract's pricing logic for client-side quote calculation.
 * It uses quad-precision (bytes16) floating point values from the contract and
 * performs iterative approximation for reverse calculations (hyUSD -> ANI).
 */

import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { JsonRpcProvider } from '@ethersproject/providers'
import Decimal from 'decimal.js'
import JSBI from 'jsbi'

import ANI_ORACLE_ABI from '../../abis/ani-oracle.json'
import ANI_TOKEN_READ_ABI from '../../abis/ani-token-read.json'
import { ANI_ORACLE_ADDRESSES, ANI_TOKEN_ADDRESSES } from '../../constants/addresses'
import { SupportedChainId } from '../../constants/chains'
import { AniGlobalParams } from '../../types/ani'
import { AniDecimal } from './constants'

/**
 * Custom error class for ANI simulation errors
 * Provides better error context for debugging and UI display
 */
export class AniSimulationError extends Error {
  constructor(
    message: string,
    public readonly code: 'INVALID_QUAD' | 'SIMULATION_FAILED' | 'PARAMS_FETCH_FAILED',
    public readonly details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'AniSimulationError'
  }
}

/**
 * Result of simulating an ANI transaction
 */
interface SimulationResult {
  newPrice: Decimal
  newStepSize: Decimal
  newStepCount: Decimal
  weightedPrice: Decimal
  usdValue: Decimal // hyUSD value in wei (1e18)
  firesale: boolean
}

// JSBI constants for quad-precision parsing
const JSBI_ZERO = JSBI.BigInt(0)
const JSBI_ONE = JSBI.BigInt(1)
const JSBI_127 = JSBI.BigInt(127)
const JSBI_112 = JSBI.BigInt(112)
const JSBI_0x7FFF = JSBI.BigInt(0x7fff)
const JSBI_BIAS = JSBI.BigInt(16383)
const JSBI_FRACTION_MASK = JSBI.subtract(JSBI.leftShift(JSBI_ONE, JSBI_112), JSBI_ONE)

/**
 * Convert bytes16 hex string (quad-precision float) to Decimal
 * IEEE 754 quadruple-precision format: 1 sign bit, 15 exponent bits, 112 fraction bits
 *
 * @throws {AniSimulationError} When the hex value represents Infinity or NaN
 */
function quadToDecimal(hexStr: string): Decimal {
  let hex = hexStr
  if (hex.startsWith('0x')) {
    hex = hex.slice(2)
  }
  hex = hex.padStart(32, '0')

  const n = JSBI.BigInt('0x' + hex)
  const sign = JSBI.bitwiseAnd(JSBI.signedRightShift(n, JSBI_127), JSBI_ONE)
  const exponent = JSBI.bitwiseAnd(JSBI.signedRightShift(n, JSBI_112), JSBI_0x7FFF)
  const fraction = JSBI.bitwiseAnd(n, JSBI_FRACTION_MASK)

  let dec: Decimal

  if (JSBI.equal(exponent, JSBI_ZERO)) {
    if (JSBI.equal(fraction, JSBI_ZERO)) {
      dec = new AniDecimal(0)
    } else {
      // Subnormal number
      const signMultiplier = JSBI.equal(sign, JSBI_ONE) ? -1 : 1
      const expValue = 1 - 16383 // 1 - bias
      dec = new AniDecimal(signMultiplier)
        .times(new AniDecimal(2).pow(expValue))
        .times(new AniDecimal(fraction.toString()).div(new AniDecimal(2).pow(112)))
    }
  } else if (JSBI.equal(exponent, JSBI_0x7FFF)) {
    // Infinity or NaN - contract returned invalid data
    throw new AniSimulationError(
      'Invalid quad-precision value from ANI contract: Infinity or NaN encountered. This may indicate contract state issues.',
      'INVALID_QUAD',
      { hexValue: hexStr, exponent: exponent.toString() }
    )
  } else {
    // Normal number
    const expValue = JSBI.toNumber(exponent) - JSBI.toNumber(JSBI_BIAS)
    const signMultiplier = JSBI.equal(sign, JSBI_ONE) ? -1 : 1
    dec = new AniDecimal(signMultiplier)
      .times(new AniDecimal(2).pow(expValue))
      .times(new AniDecimal(1).plus(new AniDecimal(fraction.toString()).div(new AniDecimal(2).pow(112))))
  }

  return dec
}

/**
 * Calculate 1.01^exponent with high precision
 */
function pow1_01(exponent: Decimal): Decimal {
  const base = new AniDecimal(101).div(100)
  return AniDecimal.exp(AniDecimal.ln(base).times(exponent))
}

/**
 * Calculate log base 1.01 of x
 */
function log1_01(x: Decimal): Decimal {
  const lnBase = AniDecimal.ln(new AniDecimal(101).div(100))
  return AniDecimal.ln(x).div(lnBase)
}

/**
 * Absolute value for Decimal
 */
function absDec(x: Decimal): Decimal {
  return x.abs()
}

/**
 * Fetch all global parameters from the ANI contract
 *
 * @throws {AniSimulationError} When contract data cannot be fetched or is invalid
 */
export async function fetchAniGlobalParams(provider: JsonRpcProvider): Promise<AniGlobalParams> {
  const aniAddress = ANI_TOKEN_ADDRESSES[SupportedChainId.HYDRA]
  const oracleAddress = ANI_ORACLE_ADDRESSES[SupportedChainId.HYDRA]
  const aniContract = new Contract(aniAddress, ANI_TOKEN_READ_ABI, provider)
  const oracleContract = new Contract(oracleAddress, ANI_ORACLE_ABI, provider)

  try {
    // Fetch bytes16 encoded values and oracle rate in parallel
    const [
      burnRateHex,
      stepSizeHex,
      priceHex,
      stepCountHex,
      initialPriceHex,
      previousPriceHex,
      previousStepCountHex,
      totalSupplyBN,
      contractUSDBalanceHex,
      rateWithContext,
    ] = await Promise.all([
      aniContract.burnRate(),
      aniContract.stepSize(),
      aniContract.price(),
      aniContract.stepCount(),
      aniContract.initialPrice(),
      aniContract.previousPrice(),
      aniContract.previousStepCount(),
      aniContract.totalSupply(),
      aniContract.contractUSDBalance(),
      oracleContract.rateWithContext(),
    ])

    // Validate and extract rateA from rateWithContext for hyUSD -> USDQ conversion
    // rateA is the rate for the primary asset (hyUSD backing)
    const oracleRateA = rateWithContext?.rateA
    if (!oracleRateA || !BigNumber.isBigNumber(oracleRateA)) {
      throw new AniSimulationError(
        'Invalid oracle rate response: rateA is missing or not a BigNumber',
        'PARAMS_FETCH_FAILED',
        { rateWithContext: JSON.stringify(rateWithContext) }
      )
    }

    // Validate totalSupply is a BigNumber
    if (!BigNumber.isBigNumber(totalSupplyBN)) {
      throw new AniSimulationError('Invalid totalSupply response: not a BigNumber', 'PARAMS_FETCH_FAILED', {
        totalSupply: String(totalSupplyBN),
      })
    }

    const params: AniGlobalParams = {
      burnRate: quadToDecimal(burnRateHex.toString()),
      stepSize: quadToDecimal(stepSizeHex.toString()),
      price: quadToDecimal(priceHex.toString()),
      stepCount: quadToDecimal(stepCountHex.toString()),
      initialPrice: quadToDecimal(initialPriceHex.toString()),
      previousPrice: quadToDecimal(previousPriceHex.toString()),
      previousStepCount: quadToDecimal(previousStepCountHex.toString()),
      tokenSupply: new AniDecimal(totalSupplyBN.toString()),
      contractUSDBalance: quadToDecimal(contractUSDBalanceHex.toString()),
      // Oracle rate using rateA from rateWithContext (1e18 scale)
      // rateA represents the USDQ value per hyUSD unit
      oracleRate: new AniDecimal(oracleRateA.toString()),
    }

    return params
  } catch (e) {
    // Re-throw AniSimulationError as-is, wrap other errors
    if (e instanceof AniSimulationError) {
      throw e
    }
    throw new AniSimulationError(
      `Failed to fetch ANI contract parameters: ${e instanceof Error ? e.message : String(e)}`,
      'PARAMS_FETCH_FAILED',
      { originalError: e instanceof Error ? e.message : String(e) }
    )
  }
}

/**
 * Simulate an ANI buy or sell transaction
 *
 * @param aniAmountWei - ANI amount in wei (1e18)
 * @param isBuy - true for buying ANI, false for selling
 * @param params - Global contract parameters
 * @param retry - Internal flag for firesale retry
 * @param prevPriceOverride - Override for previous price (used in firesale retry)
 */
// Named constants for magic numbers used in simulation
const FIRESALE_RATIO_NUMERATOR = 1000001
const FIRESALE_RATIO_DENOMINATOR = 1000000
const FEE_NUMERATOR = 1000
const FEE_DENOMINATOR_BUY = 995 // 0.5% fee for buys
const FEE_DENOMINATOR_SELL = 995 // 0.5% fee for sells
const STEP_SIZE_MULTIPLIER = '1e24'
const FEE_BURN_DIVISOR_BUY = 1000
const FEE_BURN_DIVISOR_SELL = 995
const FIRESALE_WEIGHTED_PRICE_THRESHOLD = 0.99

function simulateTransaction(
  aniAmountWei: Decimal,
  isBuy: boolean,
  params: AniGlobalParams,
  retry = false,
  prevPriceOverride: Decimal | null = null
): SimulationResult {
  const { tokenSupply, contractUSDBalance } = params
  const localBurnRate = new AniDecimal(params.burnRate)
  const localInitialPrice = new AniDecimal(params.initialPrice)
  const previousStepCount = new AniDecimal(params.previousStepCount)

  const amt = new AniDecimal(aniAmountWei)
  let A = isBuy ? amt : amt.neg()

  // Calculate ratio for firesale detection
  const ratio = tokenSupply.gt(0)
    ? contractUSDBalance.div(tokenSupply).times(FIRESALE_RATIO_NUMERATOR).div(FIRESALE_RATIO_DENOMINATOR)
    : new AniDecimal(0)

  // Use local previous price, not global
  const prevPriceLocal =
    prevPriceOverride != null ? new AniDecimal(prevPriceOverride) : new AniDecimal(params.previousPrice)

  // Firesale mode: selling when price has dropped significantly
  const fireSaleMode = !isBuy && (retry || (ratio.gte(prevPriceLocal) && A.isNegative()))

  let _B: Decimal,
    _T_new: Decimal,
    _R_new: Decimal,
    _S_new: Decimal,
    _P_new: Decimal,
    _C_new: Decimal,
    _W: Decimal,
    _V: Decimal

  if (fireSaleMode) {
    // Firesale calculation
    _B = absDec(A).div(2)
    _R_new = localBurnRate.plus(new AniDecimal(1).minus(localBurnRate).times(_B).div(tokenSupply))
    _S_new = new AniDecimal(STEP_SIZE_MULTIPLIER).times(new AniDecimal(1).minus(_R_new))
    _P_new = contractUSDBalance.div(tokenSupply)
    const I_new = localInitialPrice.times(pow1_01(_B.div(_S_new)))
    _C_new = log1_01(_P_new.div(I_new))
    _V = absDec(A).times(_P_new)
    _W = new AniDecimal(0)
  } else {
    // Normal calculation with fee adjustment
    if (isBuy) {
      A = absDec(A).times(FEE_NUMERATOR).div(FEE_DENOMINATOR_BUY)
    } else {
      A = absDec(A).times(FEE_DENOMINATOR_SELL).div(FEE_NUMERATOR)
    }
    A = isBuy ? A : A.neg()

    _B = absDec(A)
      .times(5)
      .div(isBuy ? FEE_BURN_DIVISOR_BUY : FEE_BURN_DIVISOR_SELL)
    _T_new = tokenSupply.plus(A).minus(_B)
    const maxT = tokenSupply.gt(_T_new.plus(_B)) ? tokenSupply : _T_new.plus(_B)

    _R_new = localBurnRate.plus(new AniDecimal(1).minus(localBurnRate).times(_B).div(maxT))
    _S_new = new AniDecimal(STEP_SIZE_MULTIPLIER).times(new AniDecimal(1).minus(_R_new))

    const exponent = _T_new.div(_S_new)
    _P_new = localInitialPrice.times(pow1_01(exponent))
    _C_new = log1_01(_P_new.div(localInitialPrice))

    // Calculate weighted price
    const LN_1_01 = AniDecimal.ln(new AniDecimal(101).div(100))
    const powCNew = pow1_01(_C_new)
    const powCPrev = pow1_01(previousStepCount)
    const diffC = _C_new.minus(previousStepCount)
    const diffPow = powCNew.minus(powCPrev)
    const fraction = diffC.equals(0) ? new AniDecimal(0) : diffPow.div(diffC.times(LN_1_01))
    _W = fraction.times(localInitialPrice)

    // Check if we need to retry in firesale mode
    if (_W.times(FIRESALE_WEIGHTED_PRICE_THRESHOLD).lte(ratio) && !isBuy) {
      // Retry with firesale mode
      return simulateTransaction(aniAmountWei, isBuy, params, true, ratio)
    }

    _V = absDec(A).times(_W)
    _V = isBuy
      ? absDec(_V).times(FEE_NUMERATOR).div(FEE_DENOMINATOR_BUY)
      : absDec(_V).times(FEE_DENOMINATOR_SELL).div(FEE_NUMERATOR)
  }

  return {
    newPrice: _P_new,
    newStepSize: _S_new,
    newStepCount: _C_new,
    weightedPrice: _W,
    usdValue: _V,
    firesale: fireSaleMode,
  }
}

/**
 * Forward calculation: ANI amount -> hyUSD amount
 * Used when user enters ANI amount
 *
 * @param aniAmountWei - ANI amount in wei (1e18)
 * @param isBuy - true for buying ANI, false for selling
 * @param params - Global contract parameters
 * @returns hyUSD amount in wei (1e18)
 */
export function calculateHyUsdFromAni(aniAmountWei: Decimal, isBuy: boolean, params: AniGlobalParams): Decimal {
  const result = simulateTransaction(aniAmountWei, isBuy, params)
  return result.usdValue
}

/**
 * Reverse calculation: hyUSD amount -> ANI amount
 * Uses iterative approximation since there's no direct formula
 *
 * @param hyUsdAmountWei - hyUSD amount in wei (1e18)
 * @param isBuy - true for buying ANI, false for selling
 * @param params - Global contract parameters
 * @param maxIterations - Maximum iterations for approximation
 * @returns ANI amount in wei (1e18)
 */
export function calculateAniFromHyUsd(
  hyUsdAmountWei: Decimal,
  isBuy: boolean,
  params: AniGlobalParams,
  maxIterations = 100
): Decimal {
  const tolerance = new AniDecimal('1e16') // 0.01 hyUSD tolerance
  const targetUsd = new AniDecimal(hyUsdAmountWei)

  if (targetUsd.lte(0)) {
    return new AniDecimal(0)
  }

  // Initial guess: target / current price
  const priceEstimate = params.price.gt(0) ? params.price : new AniDecimal(1)
  let mid = targetUsd.div(priceEstimate)

  // Gradient descent approximation
  for (let i = 0; i < maxIterations; i++) {
    const result = simulateTransaction(mid, isBuy, params)
    const diff = result.usdValue.minus(targetUsd)

    if (diff.abs().lte(tolerance)) {
      break
    }

    // Adjust based on difference and price
    const stepFactor = diff.div(result.newPrice).div(result.newStepSize)
    const booster = AniDecimal.pow(1.01, stepFactor)
    const adjustment = diff.div(result.newPrice).times(booster)
    mid = mid.minus(adjustment)

    // Ensure we don't go negative
    if (mid.lte(0)) {
      mid = new AniDecimal(1)
    }
  }

  return mid.floor()
}

/**
 * Budget-respecting approximation for BUY
 * Returns the maximum ANI amount that can be bought without exceeding the budget
 *
 * @param hyUsdBudgetWei - Maximum hyUSD to spend in wei (1e18)
 * @param params - Global contract parameters
 * @returns ANI amount in wei (1e18)
 */
export function calculateMaxAniForBudget(hyUsdBudgetWei: Decimal, params: AniGlobalParams): Decimal {
  const targetWei = new AniDecimal(hyUsdBudgetWei)

  if (targetWei.lte(0)) {
    return new AniDecimal(0)
  }

  // Start with initial guess
  const priceEstimate = params.price.gt(0) ? params.price : new AniDecimal(1)
  const guess = targetWei.div(priceEstimate)

  // Helper to calculate spend for a given ANI amount
  const spendFor = (aniWei: Decimal): Decimal => {
    const result = simulateTransaction(aniWei, true, params)
    return result.usdValue
  }

  // Establish bounds using doubling
  let low = new AniDecimal(0)
  let high = AniDecimal.max(guess, new AniDecimal('1'))

  let spendHigh = spendFor(high)

  // Expand upward until we exceed budget
  for (let i = 0; i < 32 && spendHigh.lte(targetWei); i++) {
    low = high
    high = high.times(2)
    spendHigh = spendFor(high)
  }

  // If still under budget, return high
  if (spendHigh.lte(targetWei)) {
    return high.floor()
  }

  // Bisection search for tightest under-budget ANI
  let bestUnder = low
  let iterations = 0

  while (iterations++ < 96 && high.minus(low).gt('1')) {
    const mid = low.plus(high).div(2)
    const spendMid = spendFor(mid)

    if (spendMid.lte(targetWei)) {
      bestUnder = mid
      low = mid
    } else {
      high = mid
    }
  }

  return bestUnder.floor()
}
